import React, { useState, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import menuData from './index.js';

export default function MenuGrid({ section }) {
  const { i18n } = useTranslation();
  const [menuItems, setMenuItems] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const isArabic = i18n.language === 'ar';

  // Simple translations
  const labels = {
    ingredients: {
      en: "Ingredients",
      pl: "Składniki",
      ar: "المكونات",
    },
    noItems: {
      en: "No menu items found.",
      pl: "Brak pozycji w menu.",
      ar: "لا توجد عناصر في القائمة.",
    }
  };

  // Load items for the given section
  useEffect(() => {
    const items = menuData[section]?.[i18n.language]?.items || [];
    setMenuItems(items);
    setExpandedIndex(null); // reset expanded when section changes
  }, [section, i18n.language]);

  const handleExpandClick = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  if (!menuItems.length) {
    return (
      <p className="text-center py-10">
        {labels.noItems[i18n.language] || labels.noItems.en}
      </p>
    );
  }

  return (
    <section 
      className={`${isArabic ? 'arabic-font text-right' : 'falafel-font text-left'}`} 
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* Items grid */}
      <div className="flex flex-wrap -mx-2">
        {menuItems.map((item, index) => (
          <article 
            key={index} 
            className="w-full sm:w-6/12 lg:w-4/12 px-2 mb-4"
          >
            <div className="border rounded-lg shadow hover:shadow-lg transition-shadow duration-200 overflow-hidden">
              
              {item.image && (
                <figure>
                  <img src={item.image} alt={`${item.name}`} title={`${item.name}`} className="w-full h-60 object-cover shadow-lg" />
                </figure>
              )}

              <div className="p-4">
                <header>
                  <h4 className="font-bold text-2xl mb-2">
                    <span
                      className={`inline-flex items-center justify-center px-3 aspect-square rounded-full bg-[#DC143C] text-white ${
                        isArabic ? 'ml-3' : 'mr-3'
                      }`}
                    >
                      {item.ID}
                    </span>
                    {item.name}
                  </h4>
                </header>
                <p>{item.description}</p>
                <p className="text-4xl text-[#DC143C]">{item.price} zł</p>
              </div>

              <footer className="flex items-center px-4 py-2 border-t">
                <button
                  className={`transform transition-transform duration-200 ${expandedIndex === index ? 'rotate-180' : 'rotate-0'}`}
                  onClick={() => handleExpandClick(index)}
                  aria-expanded={expandedIndex === index}
                  aria-controls={`ingredients-${index}`}
                >
                  <FaChevronDown />
                </button>
              </footer>

              {expandedIndex === index && (
                <section id={`ingredients-${index}`} className="p-4 border-t">
                  <h5 className="text-2xl mb-2">
                    {labels.ingredients[i18n.language] || labels.ingredients.en}:
                  </h5>
                  <p className="mb-2">
                    {Array.isArray(item.ingredients) 
                      ? item.ingredients.join(', ') 
                      : item.ingredients}
                  </p>
                </section>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
